import jsPDF from "jspdf";
import dayjs from "dayjs";
import api from "@/api";

const formatDate = (date) => {
  if (!date) return "";
  const d = date.toDate ? date.toDate() : new Date(date);
  return dayjs(d).format("DD.MM.YYYY");
};

const getLogoBase64 = async () => {
  try {
    const response = await fetch("/img/icons/android-chrome-512x512.png");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error("Could not load logo", e);
    return null;
  }
};

async function fetchImageAsBase64(url) {
  if (!url) return null;
  if (url.startsWith("data:")) return url;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.log("Direct fetch failed, trying backend proxy...", e);
    try {
      const result = await api.fetchImage({ url });
      return `data:${result.data.mimeType};base64,${result.data.base64}`;
    } catch (backendError) {
      console.error("Backend fetch failed", backendError);
      return null;
    }
  }
}

export async function generateMemberFormPDF(formData) {
  const doc = new jsPDF();
  const logoBase64 = await getLogoBase64();
  const form = formData.data || {};

  // 1. Header
  if (logoBase64) {
    doc.addImage(logoBase64, "PNG", 15, 10, 25, 25);
  }

  // Company Info
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Noormarkun VPK ry", 195, 15, { align: "right" });
  doc.text("Palotie 4", 195, 20, { align: "right" });
  doc.text("29600 Noormarkku", 195, 25, { align: "right" });
  doc.text("Sähköposti: hallitus@noormarkunvpk.fi", 195, 30, {
    align: "right",
  });

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("NOORMARKUN VPK RY – JÄSENTIETOLOMAKE", 105, 40, {
    align: "center",
  });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  const introText =
    "Tämä lomake on tarkoitettu Noormarkun VPK ry:n jäsenille. Alaikäisen kohdalla huoltajan tiedot ja suostumus ovat pakollisia.";
  const splitIntro = doc.splitTextToSize(introText, 180);
  doc.text(splitIntro, 15, 50);

  let y = 60;
  const lineH = 7;

  // 1. Jäsentiedot
  doc.setFont("helvetica", "bold");
  doc.text("1. Jäsentiedot", 15, y);
  y += lineH;
  doc.setFont("helvetica", "normal");

  const addField = (label, value) => {
    doc.text(`${label}: ${value || ""}`, 15, y);
    doc.line(15, y + 1, 195, y + 1); // Underline effect
    y += lineH;
  };

  const fName = form.firstName || formData.firstName || "";
  const lName = form.lastName || formData.lastName || "";
  addField("Nimi", `${fName} ${lName}`);
  addField("Syntymäaika", formatDate(form.dob));
  addField("Osoite", form.address);
  addField("Puhelinnumero", form.phone);
  addField("Sähköposti", form.email);
  addField("Tilinumero", form.iban);
  addField("Ruoka-aineallergiat", form.allergies);
  addField("Ajokorttiluokka", form.drivingLicense);

  // Department selection visualization
  doc.text("Osasto:", 15, y);
  const departments = [
    "Hälytysosasto",
    "Nuoriso-osasto",
    "Varhaisnuoriso-osasto",
    "Tukiosasto",
    "Veteraaniosasto",
  ];
  let depX = 40;
  let depY = y;

  departments.forEach((dep) => {
    const isSelected =
      (formData.departments || []).includes(dep) || formData.department === dep;

    // Calculate width to see if we need to wrap
    const textWidth = doc.getTextWidth(`[X] ${dep}`);

    if (depX + textWidth > 190) {
      depX = 40;
      depY += 6;
    }

    if (isSelected) {
      doc.setFont("helvetica", "bold");
      doc.text(`[X] ${dep}`, depX, depY);
      doc.setFont("helvetica", "normal");
    } else {
      doc.text(`[ ] ${dep}`, depX, depY);
    }
    depX += textWidth + 8;
  });
  y = depY + lineH + 5;

  // Check age for section title
  let isMinor = false;
  if (form.dob) {
    // try to determine if under 18
    const birthDate = dayjs(form.dob.toDate ? form.dob.toDate() : form.dob);
    const age = dayjs().diff(birthDate, "year");
    isMinor = age < 18;
  }

  // 2. Alaikäisen huoltajan tiedot
  doc.setFont("helvetica", "bold");
  const section2Title = isMinor
    ? "2. Alaikäisen huoltajan / huoltajien tiedot (pakollinen, jos jäsen on alle 18 v)"
    : "2. Lähiomaisen tiedot";
  doc.text(section2Title, 15, y);
  y += lineH;
  doc.setFont("helvetica", "normal");

  const guardianLabelPrefix = isMinor ? "Huoltajan" : "Lähiomaisen";
  addField(`${guardianLabelPrefix} nimi`, form.guardianName);
  addField(`${guardianLabelPrefix} puhelinnumero`, form.guardianPhone);
  addField(`${guardianLabelPrefix} sähköposti`, form.guardianEmail);
  y += 5;

  // 3. Suostumukset ja luvat
  doc.setFont("helvetica", "bold");
  doc.text("3. Suostumukset ja luvat", 15, y);
  y += lineH;
  doc.setFont("helvetica", "normal");

  const drawCheckbox = (label, checked) => {
    doc.rect(15, y - 4, 4, 4);
    if (checked) {
      doc.text("X", 16, y - 1);
    }
    const splitLabel = doc.splitTextToSize(label, 170);
    doc.text(splitLabel, 22, y);
    y += splitLabel.length * 5 + 2;
  };

  drawCheckbox(
    "Annan suostumukseni henkilötietojeni käsittelyyn jäsenrekisterissä ja viestintäkanavissa WhatsApp, VPK App, Secapp, Procountor sekä tietojen tallentamiseen Haka-järjestelmään.",
    form.consentDataProcessing
  );
  drawCheckbox(
    "Annan suostumukseni kuvien julkaisuun (verkkosivut, Facebook, lehdet tms.)",
    form.consentImages
  );
  drawCheckbox(
    "Annan suostumukseni nimitietojen julkaisuun palokunnan ilmoitustaululla.",
    form.consentNamePublish
  );
  drawCheckbox(
    "(Alaikäisen kohdalla) Huoltaja antaa suostumuksen yllä oleviin kohtiin.",
    form.consentGuardian
  );

  y += 5;

  // 4. Lisätiedot
  doc.setFont("helvetica", "bold");
  doc.text("4. Lisätiedot", 15, y);
  y += lineH;
  doc.setFont("helvetica", "normal");

  addField("Koulutukset/kelpoisuudet", form.qualifications);
  addField("Varusteiden kokotiedot", form.sizes);
  addField("Hälytysryhmät ja profiilit", form.alarmGroups);

  y += 5;

  // 5. Allekirjoitus
  // Check page break
  if (y + 60 > doc.internal.pageSize.getHeight()) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.text("5. Allekirjoitus", 15, y);
  y += lineH;
  doc.setFont("helvetica", "normal");

  addField("Päiväys", formatDate(formData.filledAt || new Date()));

  // Signature Image
  if (formData.signatureUrl) {
    try {
      const sigData = await fetchImageAsBase64(formData.signatureUrl);
      if (sigData) {
        doc.text("Jäsenen allekirjoitus:", 15, y + 10);
        doc.addImage(sigData, "PNG", 15, y + 12, 50, 25);
      }
    } catch (e) {
      console.error("Failed to add signature to PDF", e);
    }
  } else {
    doc.text(
      "Jäsenen allekirjoitus: ______________________________________",
      15,
      y + 20
    );
  }

  // Guardian Signature? (We'll assume the same signature field covers both or reuse it if minor)
  if (form.guardianName) {
    doc.text(
      "(Alaikäisen kohdalla) Huoltajan allekirjoitus: ______________________________________",
      80,
      y + 20
    );
  }

  y += 50;

  // Footer info
  doc.setFontSize(8);
  const footerStart = y;

  doc.text(
    "Jäsen on velvollinen ilmoittamaan muuttuneista tiedoista viipymättä oman osaston osastonjohtajalle.",
    15,
    y
  );
  y += 4;
  doc.text("• Hälytysosasto: halytysosasto@noormarkunvpk.fi", 20, y);
  y += 4;
  doc.text("• Nuoriso-osasto: nuoriso-osasto@noormarkunvpk.fi", 20, y);
  y += 4;
  doc.text(
    "• Varhaisnuoriso-osasto: varhaisnuoriso-osasto@noormarkunvpk.fi",
    20,
    y
  ); // Typo in prompt kept or fixed? kept "osato" as per prompt? No, fix typo for professionalism if obvious. "osasto"
  y += 4;
  doc.text("• Tukiosasto: tukiosasto@noormarkunvpk.fi", 20, y);
  y += 4;
  doc.text("• Veteraaniosasto: veteraaniosasto@noormarkunvpk.fi", 20, y);

  y += 8;
  doc.setFont("helvetica", "bold");
  doc.text("TIETOSUOJALAUSEKE", 15, y);
  doc.setFont("helvetica", "normal");
  y += 4;

  const privacyText =
    "Noormarkun VPK ry sitoutuu EU:n tietosuoja-asetuksen (GDPR) noudattamiseen. Henkilötietoja käsitellään vain jäsenyyden ja toiminnan edellyttämissä rajoissa. Tietoja säilytetään jäsenyyden ajan ja poistetaan, kun jäsenyys päättyy tai suostumus perutaan. Rekisteröidyllä on oikeus tarkastaa, oikaista ja poistaa tietonsa sekä perua suostumuksensa milloin tahansa ottamalla yhteyttä rekisterinpitäjään. Rekisteröidyllä on myös oikeus tehdä valitus valvontaviranomaiselle. Tietoja ei luovuteta kolmansille osapuolille ilman lainmukaista perustetta. Tietoja voidaan siirtää järjestelmiin, joita palokunta käyttää jäsenrekisterin ylläpitoon ja viestintään.";
  const splitPrivacy = doc.splitTextToSize(privacyText, 180);
  doc.text(splitPrivacy, 15, y);

  y += splitPrivacy.length * 3 + 5;

  doc.setFont("helvetica", "bold");
  doc.text("REKISTERINPITÄJÄ", 15, y);
  doc.setFont("helvetica", "normal");
  y += 4;
  doc.text("Noormarkun VPK ry, Palotie 4, 29600 Noormarkku", 15, y);
  y += 4;
  doc.text("Sähköposti: hallitus@noormarkunvpk.fi", 15, y);

  return doc;
}
